import {formatPercent} from '@deliveriesondemand/dod-utils';
import moment from 'moment-timezone';
import qs from 'query-string';

import config from '-/config/config';
import FieldErrors from '-/components/fields/FieldErrors';
import {merge, uniq} from '-/util/lodash';

const {mixpanelKeys, mode} = config;

const encodedOfferIdRE = /^[0-9a-zA-Z]{10,}$/;

const screenSize = () => {
    const width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
    const height = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
    return {width, height};
};

const isMobile = () => typeof window.orientation !== 'undefined';

const getOppositeApplicant = (applicantType = 'coApp') => {
    return applicantType === 'app' ? 'coApp' : 'app';
};

const fullRateFormat = (newRate, existingRate) => {
    return (
        <span>
            <strike>{formatPercent(existingRate, 'dynamic')}</strike> {formatPercent(newRate, 'dynamic')}
        </span>
    );
};

const renderFieldErrors = messages => {
    return messages.length ? <FieldErrors messages={messages} /> : null;
};

const compareAddresses = (address1 = {}, address2 = {}) => {
    const {formatted: formatted1 = '', assembled: assembled1 = ''} = address1;
    const address1Assembled = assembled1 || formatted1;

    const {formatted: formatted2 = '', assembled: assembled2 = ''} = address2;
    const address2Assembled = assembled2 || formatted2;

    const address1Str = address1 ? address1Assembled : false;
    const address2Str = address2 ? address2Assembled : false;
    return address1Str === address2Str;
};

const detectIE = () => {
    const ua = window.navigator.userAgent;

    const msie = ua.indexOf('MSIE ');
    if (msie > 0) {
        // IE 10 or older => return version number
        return parseInt(ua.substring(msie + 5, ua.indexOf('.', msie)), 10);
    }

    const trident = ua.indexOf('Trident/');
    if (trident > 0) {
        // IE 11 => return version number
        var rv = ua.indexOf('rv:');
        return parseInt(ua.substring(rv + 3, ua.indexOf('.', rv)), 10);
    }

    const edge = ua.indexOf('Edge/');
    if (edge > 0) {
        // Edge (IE 12+) => return version number
        return parseInt(ua.substring(edge + 5, ua.indexOf('.', edge)), 10);
    }

    // other browser
    return false;
};

const getOfferId = () => {
    const {offerId} = qs.parse(location.search);

    return offerId;
};

const getPIN = () => {
    const {pin} = qs.parse(location.search);

    return pin;
};

const getStipsMode = () => {
    const {stips} = qs.parse(location.search);

    return stips;
};

const isMeLanding = () => {
    const {t} = qs.parse(location.search);

    return t === 'me';
};

const isNewCashOffer = offerId => {
    const id = offerId || getOfferId();

    return id === 'cash';
};

const isNewFinanceOffer = offerId => {
    const id = offerId || getOfferId();

    return id === 'finance';
};

const isNewOffer = offerId => {
    const id = offerId || getOfferId();

    return ['cash', 'finance', 'new'].includes(id);
};

const isNewLanding = () => {
    const {t} = qs.parse(location.search);

    return t === 'new';
};

const isStipsBypassMode = () => {
    const {ffwd, stips} = qs.parse(location.search);

    return stips && ffwd;
};

const isDocumentsMode = () => {
    const params = qs.parse(location.search);
    const {t} = params;
    
    return t === 'documents';
};
const isStipsMode = () => {
    return !!getStipsMode();
};

const getIsolatedStipType = () => {
    const {stipType = ''} = qs.parse(location.search);
    if (stipType.includes(',')) {
        return stipType.split(',');
    } else {
        return stipType;
    }
};

const isStipsIsolationMode = () => {
    return isStipsMode() && !!getIsolatedStipType();
};

const formatAppointmentDate = (date, timezone) => {
    if (!date) {
        return '';
    }
    return moment.tz(date, timezone).format('ddd. M/DD/YY h:mm A');
};

const isReferrer = referrer => {
    const referrers = ['fb', 'eltoro'];
    return referrers.includes(referrer);
};

const getLocalStorageById = offerId => {
    const currentStorage = JSON.parse(localStorage.getItem(offerId));
    if (!currentStorage) {
        return false;
    }

    return currentStorage;
};

// safari private browsing mode throws an error if
// we try to set an item to localStorage
const setLocalStorageById = (offerId, objectAddition) => {
    try {
        const currentStorage = getLocalStorageById(offerId);
        const storageObj = merge({}, currentStorage, objectAddition);

        localStorage.setItem(getOfferId(), JSON.stringify(storageObj));
    } catch(ex) {
        return;
    }
};

const clearLocalStorageById = offerId => {
    localStorage.removeItem(offerId);
};

const setStipsWelcomeScreenShown = () => {
    const stipsWelcomeShown = {'STIPS_WELCOME_SHOWN': true};
    setLocalStorageById(getOfferId(), stipsWelcomeShown);
};

const hasStipsWelcomeScreenShown = () => {
    return !!getLocalStorageById(getOfferId()).STIPS_WELCOME_SHOWN;
};

const getDocumentElement = () => document.scrollingElement || document.documentElement;

const getInitialStep = () => {
    const {segmentId = ''} = qs.parse(location.search);
    let initialStep = 'hero';

    if (segmentId) {
        initialStep = 'segment';
    }

    return initialStep;
};

const getMixpanelId = () => {
    return mixpanelKeys[mode];
};

const formatCreditorName = (name = '') => {
    switch (name.toLowerCase()) {
        case 'evolvefcu':
        case 'evolvebg':
            return 'evolve Federal Credit Union';
        default:
            return name;
    }
};

const getCookie = name => {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
        const parts = cookies[i].split('=');
        if (parts.length === 2 && parts[0].trim() === name) {
            return decodeURIComponent(parts[1].trim());
        }
    }
    return null;
};

const getVinVehicleInfo = (vehicleInfo = {}) => {
    const {year, make, model} = vehicleInfo;
    let vinVehicleInfo = '';

    if (year && make && model) {
        vinVehicleInfo = [year, make, model].join(' ');
    }

    return vinVehicleInfo;
};

// Stable per-browser id, used as the analytics identity before an offer exists.
//
// On the new-offer route the URL carries the literal "new" / "cash" / "finance", so
// without this every visitor on that route shares one Mixpanel profile and one
// experiment bucket. Falls back to a per-page-load id when localStorage is unavailable
// (private browsing), which is worse than nothing being stored but still better than
// every visitor colliding.
const VISITOR_ID_KEY = 'refiVisitorId';
let inMemoryVisitorId = '';

const makeVisitorId = () => {
    const random = Math.random().toString(36).slice(2, 10);

    return `v-${Date.now().toString(36)}-${random}`;
};

const getVisitorId = () => {
    try {
        const existing = localStorage.getItem(VISITOR_ID_KEY);

        if (existing) {
            return existing;
        }

        const created = makeVisitorId();

        localStorage.setItem(VISITOR_ID_KEY, created);

        return created;
    } catch (ex) {
        if (!inMemoryVisitorId) {
            inMemoryVisitorId = makeVisitorId();
        }

        return inMemoryVisitorId;
    }
};

const getMyOffers = () => {
    try {
        const items = JSON.parse(localStorage.getItem('myoffers'));

        return Array.isArray(items) ? items : [];
    } catch (ex) {
        return [];
    }
};

const setMyOffers = offerId => {
    const myOfferIds = getMyOffers();
    // if we have a discovered offerId in local storage, convert to use new storage
    myOfferIds.push(offerId);
    // make sure we have a clean list of unique ids
    const uniqueOfferIds = uniq(myOfferIds.filter(id => encodedOfferIdRE.test(id)).reverse().slice(0, 10));
    // save converted to new storage type
    localStorage.setItem('myoffers', JSON.stringify(uniqueOfferIds));
};

/**
 * "This is not me" -- drop everything tying this browser to the offer it was shown, then
 * go back to the root so the creditor's configured landing decides what happens next.
 *
 * Shared by `NotMe` and the reduction landing pages, which render the same action under
 * different copy and styling. Swallows storage failures: a browser with storage blocked
 * must still be able to walk away from an offer that is not theirs.
 */
const forgetOffer = () => {
    try {
        localStorage.removeItem('offerDiscovered');
        localStorage.removeItem('myoffers');
        window.location.href = '/';
    } catch (ex) {
        return;
    }
};

export {
    forgetOffer,
    fullRateFormat,
    renderFieldErrors,
    screenSize,
    isMeLanding,
    isMobile,
    isNewCashOffer,
    isNewFinanceOffer,
    isNewOffer,
    isNewLanding,
    compareAddresses,
    detectIE,
    getOfferId,
    getPIN,
    getOppositeApplicant,
    getStipsMode,
    isDocumentsMode,
    isStipsMode,
    getIsolatedStipType,
    isStipsBypassMode,
    isStipsIsolationMode,
    formatAppointmentDate,
    isReferrer,
    setStipsWelcomeScreenShown,
    hasStipsWelcomeScreenShown,
    getDocumentElement,
    getLocalStorageById,
    setLocalStorageById,
    clearLocalStorageById,
    getMixpanelId,
    getInitialStep,
    formatCreditorName,
    getCookie,
    getVinVehicleInfo,
    encodedOfferIdRE,
    getMyOffers,
    getVisitorId,
    setMyOffers
};
